import { useEffect, useRef, useState } from "react";
import axios from "axios";

// ui
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// 新UI共通部品（cmd-po_1198: raw Dialog/MUI form → PoModalShell/Po系）
import { PoModalShell } from "../component/common/PoModalShell";
import { PoButton } from "../component/common/PoButton";
import {
  PoField,
  PoLabel,
  PoSelect,
  PoTextField,
  PoHelper,
} from "../component/common/PoForm";

// Bar
import TableTopBar from "../component/TableTopBar";
import { DataAddModalResult } from "../component/DataAddModalResult";

// jotai
import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

// API
import {
  Inquiry as InquiryType,
  getInquiries,
  getInquiry,
  postInquiry,
} from "../api/inquiry";
import { getPresignedGetPicture, postPresignedPost } from "../api/presignedURL";
import { CONST } from "../config/const";

const CATEGORIES = ["機能改善要望", "バグ報告", "操作方法の質問", "その他"];

// ── cmd-po_1198 R1: page意匠 census トークン転写用 sx ──
// .po-chip / .chip-open（catalog components.css :238-262）
const chipSx = (isOpen: boolean) => ({
  height: "22px",
  borderRadius: "11px",
  backgroundColor: isOpen
    ? "var(--sem-warning-bg, #fdf3e0)"
    : "var(--color-washi-warm, #f5f5f5)",
  color: isOpen ? "var(--sem-warning, #9c4a08)" : "var(--color-sumi, #374151)",
  "& .MuiChip-label": {
    px: "var(--sp-2, 8px)",
    fontSize: "var(--fs-11, 11px)",
    fontWeight: "var(--fw-semibold, 600)",
  },
});
// .info-alert（catalog components.css :402-403）
const infoAlertSx = {
  display: "flex",
  gap: "var(--sp-3, 12px)",
  padding: "var(--sp-3, 12px) var(--sp-4, 16px)",
  borderRadius: "var(--radius-md, 10px)",
  backgroundColor: "var(--sem-info-bg, #e8f1f8)",
  color: "var(--sem-info, #28557c)",
  fontSize: "var(--fs-12, 12px)",
  marginBottom: "var(--sp-4, 16px)",
};
// .upd-card h2（mock s18_inquiry.html :13）
const updHeadingSx = {
  fontSize: "var(--fs-13, 13px)",
  fontWeight: "var(--fw-bold, 700)",
  color: "var(--color-ai, #1e3a5f)",
  marginBottom: "var(--sp-3, 12px)",
};
// .ui-title（mock :15）
const uiTitleSx = {
  display: "block",
  fontSize: "var(--fs-12, 12px)",
  fontWeight: "var(--fw-semibold, 600)",
  color: "var(--color-sumi, #374151)",
};
// .ui-date（mock :16）
const uiDateSx = {
  display: "block",
  fontSize: "var(--fs-10, 10px)",
  color: "var(--color-sumi-muted, #9ca3af)",
};
// upd-item description（mock未モデル化=表示保全。censusはtd(=--color-sumi|12px|400)組へ合流）
const uiDescSx = {
  display: "block",
  fontSize: "var(--fs-12, 12px)",
  color: "var(--color-sumi, #374151)",
  whiteSpace: "pre-wrap" as const,
};
// .ui-ask（mock :17・F4=R5: text link意匠へ転写。onClick保全・PoButton不採用）
const uiAskSx = {
  display: "inline-flex",
  marginTop: "var(--sp-2, 8px)",
  fontSize: "var(--fs-11, 11px)",
  fontWeight: "var(--fw-semibold, 600)",
  color: "var(--color-kohaku-text, #a07608)",
  textDecoration: "none",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontFamily: "inherit",
};
// cmd-po_1198 rework(N1): 詳細モーダル chip = po-chip 全転写（root へ padding/font/gap を付与＝forte modal mapping はroot計測）
const detailChipSx = (isOpen: boolean) => ({
  ...chipSx(isOpen),
  padding: "0 var(--sp-2, 8px)",
  columnGap: "var(--sp-1, 4px)",
  fontSize: "var(--fs-11, 11px)",
  fontWeight: "var(--fw-semibold, 600)",
  "& .MuiChip-label": {
    px: 0,
    fontSize: "var(--fs-11, 11px)",
    fontWeight: "var(--fw-semibold, 600)",
  },
});
// cmd-po_1198 rework(N2): 詳細read-only行 caption→fs-11/sumi-muted, value→fs-13/sumi
const detailLabelSx = {
  fontSize: "var(--fs-11, 11px)",
  color: "var(--color-sumi-muted, #9ca3af)",
};
const detailValueSx = {
  fontSize: "var(--fs-13, 13px)",
  color: "var(--color-sumi, #374151)",
};

interface ProductUpdate {
  id: number;
  type: string;
  title: string;
  description: string;
  category?: string | null;
  release_date?: string | null;
  image_url?: string | null;
}

function Inquiry() {
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const loginUser = useAtomValue(AppAtoms.loginUser);

  const isAdminOrStaff =
    loginUser?.authority === "マスターアカウント" ||
    loginUser?.authority === "スタッフアカウント";
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);

  // 一覧
  const [inquiries, setInquiries] = useState<InquiryType[]>([]);
  // cmd-po_1151: 一覧 loading state 退役（全体 Backdrop に一本化）

  // 投稿フォームモーダル
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [postCategory, setPostCategory] = useState("");
  const [postSubject, setPostSubject] = useState("");
  const [postContent, setPostContent] = useState("");

  // 画像添付
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  // 詳細モーダル
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<InquiryType | null>(
    null,
  );
  const [detailImageUrl, setDetailImageUrl] = useState<string | null>(null);

  // 投稿成功ダイアログ
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [addModalResult, setAddModalResult] = useState('success');

  // 改修予定・改修履歴
  const [productUpdates, setProductUpdates] = useState<ProductUpdate[]>([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);

  // 画像拡大モーダル
  const [imageModalUrl, setImageModalUrl] = useState<string | null>(null);

  const fetchInquiries = async () => {
    setIsLoading(true);  // cmd-po_1151: setIsLoading に一本化（全体 Backdrop）
    const result = await getInquiries();
    if (result) {
      setInquiries(result.inquiries ?? []);
    }
    setIsLoading(false);
  };

  const fetchProductUpdates = async () => {
    setUpdatesLoading(true);
    try {
      const response = await axios.get(CONST.apiUrl2 + "productUpdates");
      setProductUpdates(response.data.product_updates ?? []);
    } catch {
      setProductUpdates([]);
    }
    setUpdatesLoading(false);
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  useEffect(() => {
    fetchProductUpdates();
  }, []);

  const handleOpenInquiryWithProduct = (item: ProductUpdate) => {
    setPostCategory("機能改善要望");
    setPostSubject(`【${item.title}について】`);
    setPostModalOpen(true);
  };

  // 画像選択
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setImageError("ファイルサイズは5MB以下にしてください");
      setSelectedFile(null);
      setImagePreviewUrl(null);
      return;
    }
    setImageError(null);
    setSelectedFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  // 投稿モーダルを閉じる（× / キャンセル 共通・cancel の reset 副作用を保全）
  const closePostModal = () => {
    setPostModalOpen(false);
    setSelectedFile(null);
    setImagePreviewUrl(null);
    setImageError(null);
  };

  // 行クリックで詳細表示
  const handleRowClick = async (inquiry: InquiryType) => {
    setDetailLoading(true);
    setDetailModalOpen(true);
    setSelectedInquiry(null);
    setDetailImageUrl(null);
    const result = await getInquiry(inquiry.id);
    if (result) {
      setSelectedInquiry(result.inquiry);
      if (result.inquiry.image_s3_key) {
        const url = await getPresignedGetPicture(result.inquiry.image_s3_key);
        if (url) setDetailImageUrl(url);
      }
    }
    setDetailLoading(false);
  };

  // 投稿フォーム送信
  const handlePostSubmit = async () => {
    if (!postCategory || !postSubject || !postContent) return;
    setPostModalOpen(false);
    setIsLoading(true);

    let image_s3_key: string | undefined = undefined;

    if (selectedFile) {
      try {
        const response = await axios.get(
          CONST.apiCognitoUrl + "presigned_post/picture",
          {
            params: {
              prefix: "inquiry",
              nowTimeName: Date.now().toString(),
              fileName: selectedFile.name,
            },
          },
        );
        const presigned_object = response.data.presigned_object as {
          fields: { [key: string]: string };
          url: string;
        };
        const destination_key = response.data.destination_key as string;

        const formData = new FormData();
        if (presigned_object?.fields) {
          Object.entries(presigned_object.fields).forEach(([k, v]) =>
            formData.append(k, v as string),
          );
        }
        formData.append("file", selectedFile);
        // S3受理確認（失敗時はimage_s3_key未設定のまま続行）
        const r = await postPresignedPost(presigned_object.url, formData);
        if (r === "seccess") {
          image_s3_key = destination_key;
        }
      } catch {
        // 画像アップロード失敗時も投稿は続行
      }
    }

    const result = await postInquiry({
      category: postCategory,
      subject: postSubject,
      content: postContent,
      ...(image_s3_key ? { image_s3_key } : {}),
    });

    setIsLoading(false);
    setPostCategory("");
    setPostSubject("");
    setPostContent("");
    setSelectedFile(null);
    setImagePreviewUrl(null);
    setImageError(null);
    fetchInquiries();
    setAddModalResult(result || 'success');
    setSuccessDialogOpen(true);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    return dateStr.replace("T", " ").substring(0, 16);
  };

  const groupByCategory = (items: ProductUpdate[]) => {
    return items.reduce(
      (acc, item) => {
        const cat = item.category ?? "未分類";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
      },
      {} as Record<string, ProductUpdate[]>,
    );
  };

  const plannedUpdates = productUpdates.filter((u) => u.type === "planned");
  const completedUpdates = productUpdates.filter((u) => u.type === "completed");

  return (
    <Box sx={{ width: "100%" }} data-testid="inquiry-page">
      <TableTopBar
        serch={false}
        serchOpen={false}
        setSerchOpen={() => {}}
        addDataNames={[]}
        addDataName={""}
        setAddDataName={() => {}}
        addCSVDataNames={[]}
        addCSVDataName={""}
        setAddCSVDataName={() => {}}
        getXLSXDataNames={[]}
        getXLSXDataName={""}
        setGetXLSXDataName={() => {}}
        editDatasNames={[]}
        editDatasName={""}
        setEditDatasName={() => {}}
        editIDs={[]}
        columnSettingOpen={false}
        openColumnSettingWindow={() => {}}
      />
      <Box sx={{ marginTop: "0px", padding: "16px" }}>
        {/* 案内文言（.info-alert 転写） */}
        <Box sx={infoAlertSx}>
          <Box
            component="span"
            sx={{ display: "inline-flex", flexShrink: 0, marginTop: "2px" }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
          <span>
            問い合わせしていただいた内容は毎週金曜日に集約し、今後のよりよいシステム作りのための参考にさせていただきます。
            <br />
            ※エントリー情報やトスアップ情報など、登録したデータに関するお問い合わせはお答えできかねます。
          </span>
        </Box>

        {/* .toolbar 転写 */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "var(--sp-2, 8px)",
            flexWrap: "wrap",
            marginBottom: "var(--sp-1, 4px)",
          }}
        >
          <Box sx={{ flex: 1 }} />
          <PoButton
            variant="primary"
            onClick={() => {
              setPostModalOpen(true);
            }}
          >
            問い合わせを投稿
          </PoButton>
        </Box>

        {/* 一覧セクション見出し */}
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: "var(--color-ai, #1e3a5f)",
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--sp-2, 8px)",
            mb: 1.5,
          }}
        >
          投稿した問い合わせ
        </Typography>

        {/* cmd-po_1151: 一覧内 CircularProgress 撤去（全体 Backdrop に一本化） */}
        {isSmartPhone ? (
          inquiries.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ textAlign: "center", py: 2.5 }}
            >
              データがありません
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {inquiries.map((inquiry) => (
                <Box
                  key={inquiry.id}
                  onClick={() => handleRowClick(inquiry)}
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: "var(--radius-md, 10px)",
                    border: "1px solid var(--color-border, #e5e5e0)",
                    p: 1.5,
                    boxShadow: "var(--shadow-card, 0 4px 16px rgba(30,58,95,0.08))",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "var(--color-sumi, #374151)",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {inquiry.subject}
                    </Typography>
                    <Chip
                      label={inquiry.status === "open" ? "未確認" : "確認済み"}
                      size="small"
                      sx={{ flexShrink: 0, ...chipSx(inquiry.status === "open") }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.25,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "11px", color: "var(--color-sumi-muted, #9ca3af)" }}
                    >
                      カテゴリ
                    </Typography>
                    <Typography sx={{ fontSize: "12px" }}>
                      {inquiry.category}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography
                      sx={{ fontSize: "11px", color: "var(--color-sumi-muted, #9ca3af)" }}
                    >
                      投稿日時
                    </Typography>
                    <Typography sx={{ fontSize: "12px" }}>
                      {formatDate(inquiry.created_at)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )
        ) : (
          <Box data-testid="table-card" sx={{
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-soft)",
            bgcolor: "var(--color-white)",
            border: "1px solid var(--color-border)",
            overflow: "hidden",
          }}>
          <Table sx={{
            "& thead .MuiTableCell-root": {
              fontSize: "11px",
              fontWeight: 600,
              color: "var(--color-ai)",
              padding: "8px 12px",
              whiteSpace: "nowrap",
              borderBottom: "1px solid var(--color-border)",
              backgroundColor: "var(--color-washi-warm)",
            },
            "& tbody .MuiTableCell-root": {
              fontSize: "12px",
              color: "var(--color-sumi)",
              padding: "8px 12px",
            },
          }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "var(--color-washi-warm)" }}>
                <TableCell>件名</TableCell>
                <TableCell>カテゴリ</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>投稿日時</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">
                      データがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                inquiries.map((inquiry) => (
                  <TableRow
                    key={inquiry.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(inquiry)}
                  >
                    <TableCell>{inquiry.subject}</TableCell>
                    <TableCell>{inquiry.category}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          inquiry.status === "open" ? "未確認" : "確認済み"
                        }
                        size="small"
                        sx={chipSx(inquiry.status === "open")}
                      />
                    </TableCell>
                    <TableCell>{formatDate(inquiry.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </Box>
        )}

        {/* 改修予定・改修履歴セクション */}
        <Box sx={{ marginTop: "40px" }}>
          {updatesLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
              {/* 今後の改修予定 */}
              <Box sx={{ flex: 1, minWidth: "280px" }}>
                <Typography component="h2" sx={updHeadingSx}>
                  【今後の改修予定】
                </Typography>
                {plannedUpdates.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    現在予定はありません
                  </Typography>
                ) : (
                  Object.entries(groupByCategory(plannedUpdates)).map(
                    ([cat, items]) => (
                      <Box key={cat} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            color: "var(--color-ai, #1e3a5f)",
                          }}
                        >
                          {cat}
                        </Typography>
                        {items.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              marginBottom: "var(--sp-3, 12px)",
                              padding: "var(--sp-3, 12px)",
                              backgroundColor: "var(--color-washi-warm, #f5f5f0)",
                              borderRadius: "var(--radius-sm, 6px)",
                              borderLeft: "3px solid var(--color-ai-light, #2d4a6f)",
                            }}
                          >
                            <Box component="span" sx={uiTitleSx}>
                              {item.title}
                            </Box>
                            {item.description && (
                              <Box component="span" sx={uiDescSx}>
                                {item.description}
                              </Box>
                            )}
                            {item.image_url && (
                              <Box sx={{ marginTop: "var(--sp-2, 8px)" }}>
                                <img
                                  src={item.image_url}
                                  alt={item.title}
                                  style={{
                                    maxWidth: "200px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    setImageModalUrl(item.image_url!)
                                  }
                                />
                              </Box>
                            )}
                            <Box
                              component="button"
                              type="button"
                              onClick={() => handleOpenInquiryWithProduct(item)}
                              sx={uiAskSx}
                            >
                              この機能について問い合わせ
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ),
                  )
                )}
              </Box>

              {/* 改修履歴 */}
              <Box sx={{ flex: 1, minWidth: "280px" }}>
                <Typography component="h2" sx={updHeadingSx}>
                  【改修履歴】
                </Typography>
                {completedUpdates.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    履歴はありません
                  </Typography>
                ) : (
                  Object.entries(groupByCategory(completedUpdates)).map(
                    ([cat, items]) => (
                      <Box key={cat} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            color: "var(--color-ai, #1e3a5f)",
                          }}
                        >
                          {cat}
                        </Typography>
                        {items.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              marginBottom: "var(--sp-3, 12px)",
                              padding: "var(--sp-3, 12px)",
                              backgroundColor: "var(--color-washi-warm, #f5f5f0)",
                              borderRadius: "var(--radius-sm, 6px)",
                              borderLeft: "3px solid var(--sem-success, #276e50)",
                            }}
                          >
                            {item.release_date && (
                              <Box component="span" sx={uiDateSx}>
                                {item.release_date}
                              </Box>
                            )}
                            <Box component="span" sx={uiTitleSx}>
                              {item.title}
                            </Box>
                            {item.description && (
                              <Box component="span" sx={uiDescSx}>
                                {item.description}
                              </Box>
                            )}
                            {item.image_url && (
                              <Box sx={{ marginTop: "var(--sp-2, 8px)" }}>
                                <img
                                  src={item.image_url}
                                  alt={item.title}
                                  style={{
                                    maxWidth: "200px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    setImageModalUrl(item.image_url!)
                                  }
                                />
                              </Box>
                            )}
                            <Box
                              component="button"
                              type="button"
                              onClick={() => handleOpenInquiryWithProduct(item)}
                              sx={uiAskSx}
                            >
                              この機能について問い合わせ
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ),
                  )
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* 投稿フォームモーダル */}
      <PoModalShell
        open={postModalOpen}
        onClose={closePostModal}
        title="問い合わせを投稿"
        testidPrefix="inquiry-post"
        frameTestId="inquiry-post-modal"
        footer={
          <>
            <PoButton variant="secondary" onClick={closePostModal}>
              キャンセル
            </PoButton>
            <PoButton
              variant="primary"
              onClick={handlePostSubmit}
              disabled={
                !postCategory || !postSubject || !postContent || !!imageError
              }
            >
              送信
            </PoButton>
          </>
        }
      >
        <PoField fieldKey="category">
          <PoLabel>カテゴリ</PoLabel>
          <PoSelect
            fullWidth
            displayEmpty
            value={postCategory}
            onChange={(e) => setPostCategory(e.target.value as string)}
          >
            {CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </PoSelect>
        </PoField>
        <PoField fieldKey="subject">
          <PoLabel>件名</PoLabel>
          <PoTextField
            fullWidth
            value={postSubject}
            onChange={(e) => setPostSubject(e.target.value)}
          />
        </PoField>
        <PoField fieldKey="content">
          <PoLabel>本文</PoLabel>
          <PoTextField
            fullWidth
            multiline
            rows={4}
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
          />
        </PoField>

        {/* 画像添付 */}
        <PoField fieldKey="image">
          <PoHelper sx={{ display: "block", marginBottom: "8px" }}>
            スクリーンショットや画像を添付いただくと、より迅速に改修対応できます
          </PoHelper>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: "none" }}
            ref={fileInputRef}
          />
          <Box>
            <PoButton
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
            >
              画像を添付
            </PoButton>
          </Box>
          {imageError && <PoHelper error>{imageError}</PoHelper>}
          {imagePreviewUrl && (
            <Box sx={{ marginTop: "8px" }}>
              <img
                src={imagePreviewUrl}
                alt="preview"
                style={{
                  maxWidth: "120px",
                  maxHeight: "120px",
                  objectFit: "contain",
                  border: "1px solid var(--color-border, #e5e5e0)",
                  borderRadius: "4px",
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {selectedFile?.name}
              </Typography>
            </Box>
          )}
        </PoField>
      </PoModalShell>

      {/* 投稿成功モーダル */}
      <DataAddModalResult
        onModal={successDialogOpen}
        swCloseModal={() => setSuccessDialogOpen(false)}
        addModalTitle="問い合わせ投稿完了"
        onDataAddModalResult={addModalResult}
      />

      {/* 詳細モーダル */}
      <PoModalShell
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={selectedInquiry?.subject ?? ""}
        testidPrefix="inquiry-detail"
        frameTestId="inquiry-detail-modal"
        footer={
          <PoButton variant="secondary" onClick={() => setDetailModalOpen(false)}>
            閉じる
          </PoButton>
        }
      >
        {detailLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : selectedInquiry ? (
          <>
            {isAdminOrStaff && selectedInquiry.vendor_company_name && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={detailLabelSx}>
                  ベンダー会社名
                </Typography>
                <Typography sx={detailValueSx}>{selectedInquiry.vendor_company_name}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={detailLabelSx}>
                カテゴリ
              </Typography>
              <Typography sx={detailValueSx}>{selectedInquiry.category}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={detailLabelSx}>
                ステータス
              </Typography>
              <Box>
                <Chip
                  label={
                    selectedInquiry.status === "open" ? "未確認" : "確認済み"
                  }
                  size="small"
                  sx={detailChipSx(selectedInquiry.status === "open")}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={detailLabelSx}>
                本文
              </Typography>
              <Typography sx={{ ...detailValueSx, whiteSpace: "pre-wrap" }}>
                {selectedInquiry.content}
              </Typography>
            </Box>
            {detailImageUrl && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={detailLabelSx}>
                  添付画像
                </Typography>
                <Box sx={{ marginTop: "4px" }}>
                  <img
                    src={detailImageUrl}
                    alt="添付画像"
                    style={{ maxWidth: "100%", borderRadius: "4px" }}
                  />
                </Box>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={detailLabelSx}>
                投稿日時
              </Typography>
              <Typography sx={detailValueSx}>
                {formatDate(selectedInquiry.created_at)}
              </Typography>
            </Box>
            {selectedInquiry.closed_at && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={detailLabelSx}>
                  対応完了日時
                </Typography>
                <Typography sx={detailValueSx}>
                  {formatDate(selectedInquiry.closed_at)}
                </Typography>
              </Box>
            )}
          </>
        ) : null}
      </PoModalShell>

      {/* 画像拡大モーダル */}
      <PoModalShell
        open={imageModalUrl !== null}
        onClose={() => setImageModalUrl(null)}
        title="画像"
        testidPrefix="inquiry-image"
        frameTestId="inquiry-image-modal"
        widthPx={760}
        footer={
          <PoButton variant="secondary" onClick={() => setImageModalUrl(null)}>
            閉じる
          </PoButton>
        }
      >
        <img
          src={imageModalUrl ?? ""}
          alt="拡大画像"
          style={{ maxWidth: "100%", maxHeight: "80vh", display: "block" }}
        />
      </PoModalShell>
    </Box>
  );
}

export default Inquiry;
